/* eslint-disable */
import { Zap, Clock, FileCode, Play, Activity, ShieldCheck, Download, Plus, CheckCircle, AlertTriangle } from "lucide-react";
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { API_BASE } from "../config/apiConfig";
import "./RealApiTesting.css";

// Sample Quick-Start Presets
const QUICK_PRESETS = [
  {
    id: "get_todo",
    name: "GET Todo Item",
    desc: "Fast JSON REST API",
    method: "GET",
    url: "https://jsonplaceholder.typicode.com/todos/1",
    headers: [{ id: "h1", key: "Accept", value: "application/json", enabled: true }],
    body: "",
    bodyMode: "none"
  },
  {
    id: "post_user",
    name: "POST Create User",
    desc: "Payload testing with JSON",
    method: "POST",
    url: "https://jsonplaceholder.typicode.com/posts",
    headers: [
      { id: "h1", key: "Content-Type", value: "application/json", enabled: true },
      { id: "h2", key: "Accept", value: "application/json", enabled: true }
    ],
    body: JSON.stringify(
      {
        title: "Qintra Real Execution",
        body: "Hardware socket tracing test with zero simulation",
        userId: 1
      },
      null,
      2
    ),
    bodyMode: "json"
  },
  {
    id: "get_ip",
    name: "GET Public IP & Socket",
    desc: "Hardware DNS & IP resolution",
    method: "GET",
    url: "https://api.ipify.org?format=json",
    headers: [{ id: "h1", key: "Accept", value: "application/json", enabled: true }],
    body: "",
    bodyMode: "none"
  },
  {
    id: "get_weather",
    name: "GET Weather API",
    desc: "Multi-parameter query test",
    method: "GET",
    url: "https://api.open-meteo.com/v1/forecast?latitude=37.77&longitude=-122.42&current_weather=true",
    headers: [{ id: "h1", key: "Accept", value: "application/json", enabled: true }],
    body: "",
    bodyMode: "none"
  },
  {
    id: "get_404",
    name: "GET 404 Status Guidance",
    desc: "Error guidance & diagnostics",
    method: "GET",
    url: "https://httpstat.us/404",
    headers: [{ id: "h1", key: "Accept", value: "application/json", enabled: true }],
    body: "",
    bodyMode: "none"
  }
];

export default function RealApiTesting() {
  // Main Navigation Tab
  const [activeTab, setActiveTab] = useState("single"); // "single" | "load" | "security"

  // URL & Method
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("https://jsonplaceholder.typicode.com/todos/1");

  // Request Sub-Tabs
  const [requestTab, setRequestTab] = useState("params"); // "params" | "headers" | "body" | "auth" | "assertions" | "settings"

  // Query Parameters
  const [queryParams, setQueryParams] = useState([
    { id: "p1", key: "", value: "", enabled: true, desc: "" }
  ]);
  const [paramsBulkEdit, setParamsBulkEdit] = useState(false);
  const [paramsBulkText, setParamsBulkText] = useState("");

  // Headers
  const [headersList, setHeadersList] = useState([
    { id: "h1", key: "Accept", value: "application/json", enabled: true, desc: "Default JSON" }
  ]);
  const [headersBulkEdit, setHeadersBulkEdit] = useState(false);
  const [headersBulkText, setHeadersBulkText] = useState('{\n  "Accept": "application/json"\n}');

  // Body
  const [bodyMode, setBodyMode] = useState("none"); // "none" | "json" | "form" | "raw"
  const [bodyText, setBodyText] = useState('{\n  "name": "Qintra Real Engine",\n  "status": "active"\n}');

  // Auth
  const [authType, setAuthType] = useState("none"); // "none" | "bearer" | "basic" | "api_key"
  const [authToken, setAuthToken] = useState("");
  const [authUsername, setAuthUsername] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [apiKeyName, setApiKeyName] = useState("X-API-Key");
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [apiKeyLocation, setApiKeyLocation] = useState("header"); // "header" | "query"

  // Assertions
  const [assertionsList, setAssertionsList] = useState([
    { id: "a1", target: "status_code", operator: "equals", expected: "200", enabled: true },
    { id: "a2", target: "total_duration", operator: "less_than", expected: "2000", enabled: true }
  ]);

  // Settings
  const [timeoutSeconds, setTimeoutSeconds] = useState(15);
  const [verifySsl, setVerifySsl] = useState(true);

  // Execution States
  const [loading, setLoading] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [responseTab, setResponseTab] = useState("body"); // "body" | "waterfall" | "headers" | "assertions" | "code"
  const [responseBodyView, setResponseBodyView] = useState("pretty"); // "pretty" | "raw"
  const [codeLang, setCodeLang] = useState("curl"); // "curl" | "fetch" | "axios" | "python" | "go"
  const [copiedField, setCopiedField] = useState(null);

  // History & Presets
  const [history, setHistory] = useState([]);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);

  // Import Modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState("curl"); // "curl" | "postman" | "openapi"
  const [importInput, setImportInput] = useState("");

  // Load Testing State
  const [loadUsers, setLoadUsers] = useState(5);
  const [loadRequests, setLoadRequests] = useState(25);
  const [loadResult, setLoadResult] = useState(null);

  // Security Audit State
  const [securityResult, setSecurityResult] = useState(null);

  // Load history from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("qintra_real_api_history");
      if (saved) {
        setHistory(JSON.parse(saved));
      }
    } catch {
      // ignore
    }
  }, []);

  const saveToHistory = (item) => {
    try {
      const nextHistory = [item, ...history.filter((h) => h.id !== item.id)].slice(0, 25);
      setHistory(nextHistory);
      localStorage.setItem("qintra_real_api_history", JSON.stringify(nextHistory));
    } catch {
      // ignore
    }
  };

  // Keyboard shortcut Ctrl+Enter to execute
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (!loading && activeTab === "single") {
          handleExecuteSingle();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  // Safe JSON Parser
  const parseJsonSafe = (str, fallback = {}) => {
    try {
      return JSON.parse(str);
    } catch {
      return fallback;
    }
  };

  // Build Headers Object
  const getCompiledHeaders = () => {
    if (headersBulkEdit) {
      return parseJsonSafe(headersBulkText, {});
    }
    const hdrs = {};
    headersList.forEach((h) => {
      if (h.enabled && h.key && h.key.trim()) {
        hdrs[h.key.trim()] = h.value || "";
      }
    });

    // Merge Auth Headers
    if (authType === "bearer" && authToken.trim()) {
      hdrs["Authorization"] = `Bearer ${authToken.trim()}`;
    } else if (authType === "basic" && (authUsername || authPassword)) {
      const creds = btoa(`${authUsername}:${authPassword}`);
      hdrs["Authorization"] = `Basic ${creds}`;
    } else if (authType === "api_key" && apiKeyLocation === "header" && apiKeyName.trim()) {
      hdrs[apiKeyName.trim()] = apiKeyValue || "";
    }

    return hdrs;
  };

  // Build Params Object
  const getCompiledParams = () => {
    if (paramsBulkEdit) {
      return parseJsonSafe(paramsBulkText, {});
    }
    const params = {};
    queryParams.forEach((p) => {
      if (p.enabled && p.key && p.key.trim()) {
        params[p.key.trim()] = p.value || "";
      }
    });
    if (authType === "api_key" && apiKeyLocation === "query" && apiKeyName.trim()) {
      params[apiKeyName.trim()] = apiKeyValue || "";
    }
    return params;
  };

  // Single Real API Execution
  const handleExecuteSingle = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setExecutionResult(null);

    const compiledHeaders = getCompiledHeaders();
    const compiledParams = getCompiledParams();
    const compiledAssertions = assertionsList
      .filter((a) => a.enabled && a.target)
      .map((a) => ({
        target: a.target,
        operator: a.operator,
        expected: isNaN(a.expected) ? a.expected : Number(a.expected)
      }));

    let requestBody = null;
    if (method !== "GET" && bodyMode !== "none") {
      requestBody = bodyMode === "json" ? parseJsonSafe(bodyText, bodyText) : bodyText;
    }

    try {
      const authConfig = {
        type: authType,
        token: authToken,
        username: authUsername,
        password: authPassword,
        api_key_name: apiKeyName,
        api_key_value: apiKeyValue,
        api_key_location: apiKeyLocation
      };

      const res = await axios.post(`${API_BASE}/api/v2/real-api/execute`, {
        url: url.trim(),
        method,
        headers: compiledHeaders,
        params: compiledParams,
        body: requestBody,
        auth: authConfig,
        assertions: compiledAssertions,
        verify_ssl: verifySsl,
        timeout_seconds: Number(timeoutSeconds)
      });

      setExecutionResult(res.data);

      // Save into history
      saveToHistory({
        id: "hist_" + Date.now(),
        timestamp: new Date().toLocaleTimeString(),
        method,
        url,
        status: res.data.execution?.http_status || (res.data.success ? 200 : 500),
        duration: res.data.execution?.metrics?.total_duration_ms || 0
      });
    } catch (err) {
      const errRes = {
        success: false,
        error: err.response?.data?.detail || err.message,
        execution: {
          http_status: err.response?.status || "FAILED",
          metrics: { total_duration_ms: 0 },
          response: { body_raw: err.message },
          guidance: "Network connection refused or server unreachable. Ensure endpoint is running and reachable."
        }
      };
      setExecutionResult(errRes);
    } finally {
      setLoading(false);
    }
  };

  // Apply Quick Preset
  const handleApplyPreset = (preset) => {
    setMethod(preset.method);
    setUrl(preset.url);
    setHeadersList(preset.headers);
    setBodyText(preset.body || "");
    setBodyMode(preset.bodyMode || "none");
    if (preset.params) {
      setQueryParams(preset.params);
    }
  };

  // Import Handler (cURL, Postman, OpenAPI)
  const handleExecuteImport = async () => {
    if (!importInput.trim()) return;
    setLoading(true);
    try {
      if (importType === "curl") {
        const res = await axios.post(`${API_BASE}/api/v2/real-api/import/curl`, {
          curl_command: importInput.trim()
        });
        if (res.data.success && res.data.parsed_test) {
          const p = res.data.parsed_test;
          setUrl(p.url || url);
          setMethod(p.method || "GET");

          const hdrs = { ...(p.headers || {}) };
          const authVal = hdrs.authorization || hdrs.Authorization || "";
          if (authVal.toLowerCase().startsWith("bearer ")) {
            setAuthType("bearer");
            setAuthToken(authVal.slice(7).trim());
            delete hdrs.authorization;
            delete hdrs.Authorization;
          }

          const formattedHeaders = Object.entries(hdrs).map(([k, v], idx) => ({
            id: "h_imp_" + idx,
            key: k,
            value: String(v),
            enabled: true
          }));
          setHeadersList(formattedHeaders.length ? formattedHeaders : [{ id: "h1", key: "Accept", value: "application/json", enabled: true }]);

          if (p.body) {
            setBodyMode(typeof p.body === "object" ? "json" : "raw");
            setBodyText(typeof p.body === "string" ? p.body : JSON.stringify(p.body, null, 2));
          }
          setShowImportModal(false);
          setImportInput("");
        }
      } else if (importType === "postman") {
        const parsedJson = JSON.parse(importInput);
        const res = await axios.post(`${API_BASE}/api/v2/real-api/import/postman`, {
          collection: parsedJson
        });
        if (res.data.success && res.data.test_cases?.length > 0) {
          const firstTest = res.data.test_cases[0];
          setUrl(firstTest.url || url);
          setMethod(firstTest.method || "GET");
          setShowImportModal(false);
          setImportInput("");
          alert(`Successfully imported ${res.data.total_imported} test cases from Postman collection!`);
        }
      } else if (importType === "openapi") {
        const parsedJson = JSON.parse(importInput);
        const res = await axios.post(`${API_BASE}/api/v2/real-api/import/openapi`, {
          spec: parsedJson
        });
        if (res.data.success && res.data.test_cases?.length > 0) {
          const firstTest = res.data.test_cases[0];
          setUrl(firstTest.url || url);
          setMethod(firstTest.method || "GET");
          setShowImportModal(false);
          setImportInput("");
          alert(`Successfully imported ${res.data.total_imported} test cases from OpenAPI specification!`);
        }
      }
    } catch (err) {
      alert("Import Failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Run Real Load Test
  const handleRunLoadTest = async () => {
    setLoading(true);
    setLoadResult(null);
    try {
      const res = await axios.post(`${API_BASE}/api/v2/real-api/load-test/run`, {
        url: url.trim(),
        method,
        headers: getCompiledHeaders(),
        concurrent_users: Number(loadUsers),
        total_requests: Number(loadRequests)
      });
      setLoadResult(res.data.load_test_results);
    } catch (err) {
      alert("Load Test Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Run OWASP Security Audit
  const handleRunSecurityScan = async () => {
    setLoading(true);
    setSecurityResult(null);
    try {
      const res = await axios.post(`${API_BASE}/api/v2/real-api/security-scan/run`, {
        url: url.trim(),
        method,
        headers: getCompiledHeaders()
      });
      setSecurityResult(res.data.security_scan_results);
    } catch (err) {
      alert("Security Scan Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Copy helper
  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 1800);
  };

  // Format Bytes helper
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const metrics = executionResult?.execution?.metrics || {};
  const statusNumber = Number(executionResult?.execution?.http_status) || 0;
  const statusClass =
    statusNumber >= 200 && statusNumber < 300
      ? "status-2xx"
      : statusNumber >= 300 && statusNumber < 400
        ? "status-3xx"
        : statusNumber >= 400 && statusNumber < 500
          ? "status-4xx"
          : statusNumber >= 500
            ? "status-5xx"
            : "status-err";

  // Code Export Snippets Generator
  const generatedCode = useMemo(() => {
    const compiledHdrs = getCompiledHeaders();
    if (codeLang === "curl") {
      let cmd = `curl -X ${method} "${url}"`;
      Object.entries(compiledHdrs).forEach(([k, v]) => {
        cmd += ` \\\n  -H "${k}: ${v}"`;
      });
      if (method !== "GET" && bodyMode !== "none" && bodyText) {
        cmd += ` \\\n  -d '${bodyText.replace(/'/g, "\\'")}'`;
      }
      return cmd;
    }
    if (codeLang === "fetch") {
      const options = {
        method,
        headers: compiledHdrs
      };
      if (method !== "GET" && bodyMode !== "none" && bodyText) {
        options.body = bodyMode === "json" ? JSON.parse(bodyText || "{}") : bodyText;
      }
      return `fetch("${url}", ${JSON.stringify(options, null, 2)})\n  .then(res => res.json())\n  .then(data => console.log(data))\n  .catch(err => console.error(err));`;
    }
    if (codeLang === "axios") {
      return `import axios from 'axios';\n\naxios({\n  method: '${method.toLowerCase()}',\n  url: '${url}',\n  headers: ${JSON.stringify(compiledHdrs, null, 2)},\n  ${method !== "GET" && bodyMode !== "none" ? `data: ${bodyText || "{}"}` : ""}\n})\n.then(response => console.log(response.data))\n.catch(error => console.error(error));`;
    }
    if (codeLang === "python") {
      return `import requests\n\nheaders = ${JSON.stringify(compiledHdrs, null, 2)}\n${method !== "GET" && bodyMode !== "none" ? `payload = ${bodyText || "{}"}\n` : ""}response = requests.${method.toLowerCase()}(\n    "${url}",\n    headers=headers${method !== "GET" && bodyMode !== "none" ? ",\n    json=payload" : ""}\n)\n\nprint(response.status_code)\nprint(response.json())`;
    }
    if (codeLang === "go") {
      return `package main\n\nimport (\n\t"fmt"\n\t"net/http"\n\t"io/ioutil"\n)\n\nfunc main() {\n\tclient := &http.Client{}\n\treq, err := http.NewRequest("${method}", "${url}", nil)\n\tif err != nil {\n\t\tpanic(err)\n\t}\n\tres, err := client.Do(req)\n\tif err != nil {\n\t\tpanic(err)\n\t}\n\tdefer res.Body.Close()\n\tbody, _ := ioutil.ReadAll(res.Body)\n\tfmt.Println(string(body))\n}`;
    }
    return "";
  }, [codeLang, method, url, headersList, bodyMode, bodyText, authType, authToken, authUsername, authPassword]);

  return (
    <div className="realapi-container">
      {/* 1. HERO HEADER BANNER */}
      <div className="realapi-hero">
        <div className="realapi-hero-left">
          <div className="realapi-hero-icon"><Zap size={24} color="#FD6101" /></div>
          <div className="realapi-hero-title-group">
            <h1>
              Real Execution API Engine
              <span className="realapi-mandate-badge">
                <span className="realapi-pulse-dot" style={{ backgroundColor: "#ffffff" }}></span>
                Zero Simulation Mandate
              </span>
            </h1>
            <p className="realapi-hero-subtext">
              100% Real Network Execution • Low-Level Socket Phase Diagnostics • Zero Fake Timings
            </p>
          </div>
        </div>

        <div className="realapi-hero-right">
          <div className="realapi-stat-pill">
            <span className="realapi-pulse-dot"></span>
            <span>Real Sockets Active</span>
          </div>

          <button
            type="button"
            className={`realapi-action-btn ${showHistoryDrawer ? "active" : ""}`}
            onClick={() => setShowHistoryDrawer(!showHistoryDrawer)}
            title="View Real Execution History"
          >
            <Clock size={14} style={{ display: "inline", marginRight: "6px", verticalAlign: "middle" }} /> History ({history.length})
          </button>

          <button
            type="button"
            className="realapi-action-btn"
            onClick={() => setShowImportModal(true)}
            title="Import cURL, Postman Collection, or OpenAPI"
          >
            <FileCode size={14} style={{ display: "inline", marginRight: "6px", verticalAlign: "middle" }} /> Import
          </button>
        </div>
      </div>

      {/* 2. MAIN TABS & QUICK PRESETS */}
      <div className="realapi-nav-tabs-wrapper">
        <div className="realapi-nav-tabs">
          <button
            type="button"
            className={`realapi-nav-tab ${activeTab === "single" ? "active" : ""}`}
            onClick={() => setActiveTab("single")}
          >
            <Play size={14} style={{ display: "inline", marginRight: "6px", verticalAlign: "middle" }} /> Real API Runner
          </button>
          <button
            type="button"
            className={`realapi-nav-tab ${activeTab === "load" ? "active" : ""}`}
            onClick={() => setActiveTab("load")}
          >
            <Activity size={14} style={{ display: "inline", marginRight: "6px", verticalAlign: "middle" }} /> Real Load Test
          </button>
          <button
            type="button"
            className={`realapi-nav-tab ${activeTab === "security" ? "active" : ""}`}
            onClick={() => setActiveTab("security")}
          >
            <ShieldCheck size={14} style={{ display: "inline", marginRight: "6px", verticalAlign: "middle" }} /> OWASP Security Audit
          </button>
        </div>

        {activeTab === "single" && (
          <div className="realapi-quick-presets">
            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
              Quick Presets:
            </span>
            {QUICK_PRESETS.slice(0, 3).map((p) => (
              <button
                key={p.id}
                type="button"
                className="realapi-preset-pill"
                onClick={() => handleApplyPreset(p)}
                title={p.desc}
              >
                <span style={{ fontWeight: 800, color: p.method === "POST" ? "#FD6101" : "#059669" }}>{p.method}</span>
                <span>{p.name.replace(/^[A-Z]+\s/, "")}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 3. REAL API RUNNER WORKSPACE */}
      {activeTab === "single" && (
        <>
          {/* Integrated Command Bar */}
          <div className="realapi-command-bar">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className={`realapi-method-select method-${method.toLowerCase()}`}
              title="HTTP Request Method"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
              <option value="OPTIONS">OPTIONS</option>
              <option value="HEAD">HEAD</option>
            </select>

            <div className="realapi-url-wrapper">
              <span className="realapi-url-prefix-icon" title="HTTP/HTTPS Target Viewport">
                🔒
              </span>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.example.com/v1/endpoint"
                className="realapi-url-input"
                title="Target API Endpoint URL"
              />
              {url && url.length > 0 && (
                <button
                  type="button"
                  className="realapi-url-clear-btn"
                  onClick={() => setUrl("")}
                  title="Clear URL"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="button"
              className="realapi-send-btn"
              onClick={handleExecuteSingle}
              disabled={loading || !url.trim()}
              title="Execute Real HTTP Request (Ctrl + Enter)"
            >
              {loading ? (
                <>
                  <span className="realapi-pulse-dot" style={{ backgroundColor: "#ffffff" }}></span>
                  <span>Executing...</span>
                </>
              ) : (
                <>
                  <span>Send Request</span>
                  <span className="realapi-send-shortcut">Ctrl+↵</span>
                </>
              )}
            </button>
          </div>

          {/* Two-Panel Studio Grid */}
          <div className="realapi-studio-grid">
            {/* LEFT CARD: REQUEST CONFIGURATION STUDIO */}
            <div className="realapi-card">
              <div className="realapi-card-header">
                <h3 className="realapi-card-title">
                  <span style={{ color: "#FD6101" }}>⚙️</span> Request Configuration
                </h3>

                <div className="realapi-sub-tabs">
                  <button
                    type="button"
                    className={`realapi-sub-tab ${requestTab === "params" ? "active" : ""}`}
                    onClick={() => setRequestTab("params")}
                  >
                    Params
                    <span className="realapi-badge-count">
                      {queryParams.filter((p) => p.enabled && p.key).length}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`realapi-sub-tab ${requestTab === "headers" ? "active" : ""}`}
                    onClick={() => setRequestTab("headers")}
                  >
                    Headers
                    <span className="realapi-badge-count">
                      {headersList.filter((h) => h.enabled && h.key).length}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`realapi-sub-tab ${requestTab === "body" ? "active" : ""}`}
                    onClick={() => setRequestTab("body")}
                  >
                    Body
                    {bodyMode !== "none" && (
                      <span className="realapi-badge-count" style={{ textTransform: "uppercase" }}>
                        {bodyMode}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`realapi-sub-tab ${requestTab === "auth" ? "active" : ""}`}
                    onClick={() => setRequestTab("auth")}
                  >
                    Auth
                    {authType !== "none" && (
                      <span className="realapi-badge-count" style={{ textTransform: "capitalize" }}>
                        {authType}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`realapi-sub-tab ${requestTab === "assertions" ? "active" : ""}`}
                    onClick={() => setRequestTab("assertions")}
                  >
                    Assertions
                    <span className="realapi-badge-count">{assertionsList.length}</span>
                  </button>
                  <button
                    type="button"
                    className={`realapi-sub-tab ${requestTab === "settings" ? "active" : ""}`}
                    onClick={() => setRequestTab("settings")}
                  >
                    Settings
                  </button>
                </div>
              </div>

              <div className="realapi-card-body">
                {/* 1. QUERY PARAMS TAB */}
                {requestTab === "params" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                        Query Parameters
                      </span>
                      <button
                        type="button"
                        className="realapi-link-btn"
                        onClick={() => setParamsBulkEdit(!paramsBulkEdit)}
                      >
                        {paramsBulkEdit ? "Key-Value Mode" : "Bulk Edit (JSON)"}
                      </button>
                    </div>

                    {paramsBulkEdit ? (
                      <textarea
                        value={paramsBulkText}
                        onChange={(e) => setParamsBulkText(e.target.value)}
                        placeholder='{\n  "query": "value"\n}'
                        rows={6}
                        className="realapi-body-editor"
                        style={{ minHeight: "180px" }}
                      />
                    ) : (
                      <>
                        <table className="realapi-kv-table">
                          <thead>
                            <tr>
                              <th style={{ width: "30px" }}></th>
                              <th>Key</th>
                              <th>Value</th>
                              <th>Description</th>
                              <th style={{ width: "34px" }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {queryParams.map((param, index) => (
                              <tr key={param.id || index}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={param.enabled}
                                    onChange={(e) => {
                                      const next = [...queryParams];
                                      next[index].enabled = e.target.checked;
                                      setQueryParams(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={param.key}
                                    placeholder="Param Key"
                                    className="realapi-kv-input"
                                    onChange={(e) => {
                                      const next = [...queryParams];
                                      next[index].key = e.target.value;
                                      setQueryParams(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={param.value}
                                    placeholder="Value"
                                    className="realapi-kv-input"
                                    onChange={(e) => {
                                      const next = [...queryParams];
                                      next[index].value = e.target.value;
                                      setQueryParams(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={param.desc || ""}
                                    placeholder="Optional note"
                                    className="realapi-kv-input"
                                    onChange={(e) => {
                                      const next = [...queryParams];
                                      next[index].desc = e.target.value;
                                      setQueryParams(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="realapi-kv-del-btn"
                                    onClick={() => {
                                      const next = queryParams.filter((_, idx) => idx !== index);
                                      setQueryParams(next.length ? next : [{ id: "p1", key: "", value: "", enabled: true }]);
                                    }}
                                    title="Delete Param"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <button
                          type="button"
                          className="realapi-link-btn"
                          onClick={() =>
                            setQueryParams([
                              ...queryParams,
                              { id: "p_" + Date.now(), key: "", value: "", enabled: true }
                            ])
                          }
                        >
                          + Add Query Parameter
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* 2. HEADERS TAB */}
                {requestTab === "headers" && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                        HTTP Request Headers
                      </span>
                      <button
                        type="button"
                        className="realapi-link-btn"
                        onClick={() => setHeadersBulkEdit(!headersBulkEdit)}
                      >
                        {headersBulkEdit ? "Key-Value Mode" : "Bulk Edit (JSON)"}
                      </button>
                    </div>

                    {headersBulkEdit ? (
                      <textarea
                        value={headersBulkText}
                        onChange={(e) => setHeadersBulkText(e.target.value)}
                        placeholder='{\n  "Accept": "application/json"\n}'
                        rows={6}
                        className="realapi-body-editor"
                        style={{ minHeight: "180px" }}
                      />
                    ) : (
                      <>
                        <table className="realapi-kv-table">
                          <thead>
                            <tr>
                              <th style={{ width: "30px" }}></th>
                              <th>Header</th>
                              <th>Value</th>
                              <th>Description</th>
                              <th style={{ width: "34px" }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {headersList.map((header, index) => (
                              <tr key={header.id || index}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={header.enabled}
                                    onChange={(e) => {
                                      const next = [...headersList];
                                      next[index].enabled = e.target.checked;
                                      setHeadersList(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={header.key}
                                    placeholder="Header Name"
                                    className="realapi-kv-input"
                                    onChange={(e) => {
                                      const next = [...headersList];
                                      next[index].key = e.target.value;
                                      setHeadersList(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={header.value}
                                    placeholder="Value"
                                    className="realapi-kv-input"
                                    onChange={(e) => {
                                      const next = [...headersList];
                                      next[index].value = e.target.value;
                                      setHeadersList(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    value={header.desc || ""}
                                    placeholder="Optional note"
                                    className="realapi-kv-input"
                                    onChange={(e) => {
                                      const next = [...headersList];
                                      next[index].desc = e.target.value;
                                      setHeadersList(next);
                                    }}
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="realapi-kv-del-btn"
                                    onClick={() => {
                                      const next = headersList.filter((_, idx) => idx !== index);
                                      setHeadersList(next.length ? next : [{ id: "h1", key: "Accept", value: "application/json", enabled: true }]);
                                    }}
                                    title="Delete Header"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="realapi-table-actions">
                          <button
                            type="button"
                            className="realapi-link-btn"
                            onClick={() =>
                              setHeadersList([
                                ...headersList,
                                { id: "h_" + Date.now(), key: "", value: "", enabled: true }
                              ])
                            }
                          >
                            + Add Header
                          </button>

                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              className="realapi-preset-pill"
                              onClick={() => {
                                if (!headersList.some((h) => h.key.toLowerCase() === "content-type")) {
                                  setHeadersList([
                                    ...headersList,
                                    { id: "h_ct", key: "Content-Type", value: "application/json", enabled: true }
                                  ]);
                                }
                              }}
                            >
                              + Content-Type: JSON
                            </button>
                            <button
                              type="button"
                              className="realapi-preset-pill"
                              onClick={() => {
                                if (!headersList.some((h) => h.key.toLowerCase() === "user-agent")) {
                                  setHeadersList([
                                    ...headersList,
                                    { id: "h_ua", key: "User-Agent", value: "Qintra-RealExecution/2.0", enabled: true }
                                  ]);
                                }
                              }}
                            >
                              + Qintra User-Agent
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* 3. BODY TAB */}
                {requestTab === "body" && (
                  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <div className="realapi-body-types">
                      {[
                        { id: "none", label: "None" },
                        { id: "json", label: "JSON" },
                        { id: "raw", label: "Raw Text" }
                      ].map((type) => (
                        <label key={type.id} className="realapi-radio-label">
                          <input
                            type="radio"
                            name="bodyMode"
                            checked={bodyMode === type.id}
                            onChange={() => setBodyMode(type.id)}
                          />
                          <span>{type.label}</span>
                        </label>
                      ))}

                      {bodyMode === "json" && (
                        <button
                          type="button"
                          className="realapi-link-btn"
                          style={{ marginLeft: "auto" }}
                          onClick={() => {
                            try {
                              const parsed = JSON.parse(bodyText);
                              setBodyText(JSON.stringify(parsed, null, 2));
                            } catch {
                              alert("Invalid JSON format");
                            }
                          }}
                        >
                          ✨ Beautify JSON
                        </button>
                      )}
                    </div>

                    {bodyMode === "none" ? (
                      <div style={{ padding: "40px 0", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                        This request does not include a request payload body.
                      </div>
                    ) : (
                      <textarea
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                        placeholder="Enter request payload body..."
                        className="realapi-body-editor"
                      />
                    )}
                  </div>
                )}

                {/* 4. AUTH TAB */}
                {requestTab === "auth" && (
                  <div className="realapi-auth-box">
                    <label style={{ fontSize: "12px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "8px" }}>
                      Authentication Scheme
                    </label>
                    <select
                      value={authType}
                      onChange={(e) => setAuthType(e.target.value)}
                      className="realapi-kv-input"
                      style={{ marginBottom: "16px", height: "40px" }}
                    >
                      <option value="none">No Auth (Public Endpoint)</option>
                      <option value="bearer">Bearer Token</option>
                      <option value="basic">Basic Auth (Username / Password)</option>
                      <option value="api_key">API Key</option>
                    </select>

                    {authType === "bearer" && (
                      <div>
                        <label style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                          Token String
                        </label>
                        <input
                          type="text"
                          value={authToken}
                          onChange={(e) => setAuthToken(e.target.value)}
                          placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                          className="realapi-kv-input"
                        />
                      </div>
                    )}

                    {authType === "basic" && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                        <div>
                          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                            Username
                          </label>
                          <input
                            type="text"
                            value={authUsername}
                            onChange={(e) => setAuthUsername(e.target.value)}
                            placeholder="admin"
                            className="realapi-kv-input"
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                            Password
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type={showPassword ? "text" : "password"}
                              value={authPassword}
                              onChange={(e) => setAuthPassword(e.target.value)}
                              placeholder="••••••••"
                              className="realapi-kv-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: "12px" }}
                            >
                              {showPassword ? "🙈" : "👁️"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {authType === "api_key" && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                        <div>
                          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                            Key Name
                          </label>
                          <input
                            type="text"
                            value={apiKeyName}
                            onChange={(e) => setApiKeyName(e.target.value)}
                            placeholder="X-API-Key"
                            className="realapi-kv-input"
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                            Value
                          </label>
                          <input
                            type="text"
                            value={apiKeyValue}
                            onChange={(e) => setApiKeyValue(e.target.value)}
                            placeholder="key_secret_123"
                            className="realapi-kv-input"
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: "11px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                            Add To
                          </label>
                          <select
                            value={apiKeyLocation}
                            onChange={(e) => setApiKeyLocation(e.target.value)}
                            className="realapi-kv-input"
                          >
                            <option value="header">Header</option>
                            <option value="query">Query Params</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 5. ASSERTIONS TAB */}
                {requestTab === "assertions" && (
                  <div>
                    <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: "12px" }}>
                      Automated Assertions & Validation Rules
                    </span>

                    <table className="realapi-kv-table">
                      <thead>
                        <tr>
                          <th style={{ width: "30px" }}></th>
                          <th>Target</th>
                          <th>Operator</th>
                          <th>Expected Value</th>
                          <th style={{ width: "34px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {assertionsList.map((assertion, index) => (
                          <tr key={assertion.id || index}>
                            <td>
                              <input
                                type="checkbox"
                                checked={assertion.enabled}
                                onChange={(e) => {
                                  const next = [...assertionsList];
                                  next[index].enabled = e.target.checked;
                                  setAssertionsList(next);
                                }}
                              />
                            </td>
                            <td>
                              <select
                                value={assertion.target}
                                onChange={(e) => {
                                  const next = [...assertionsList];
                                  next[index].target = e.target.value;
                                  setAssertionsList(next);
                                }}
                                className="realapi-kv-input"
                              >
                                <option value="status_code">HTTP Status Code</option>
                                <option value="total_duration">Total Duration (ms)</option>
                                <option value="body_contains">Response Contains String</option>
                              </select>
                            </td>
                            <td>
                              <select
                                value={assertion.operator}
                                onChange={(e) => {
                                  const next = [...assertionsList];
                                  next[index].operator = e.target.value;
                                  setAssertionsList(next);
                                }}
                                className="realapi-kv-input"
                              >
                                <option value="equals">Equals</option>
                                <option value="less_than">Less Than (&lt;)</option>
                                <option value="greater_than">Greater Than (&gt;)</option>
                                <option value="contains">Contains</option>
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={assertion.expected}
                                onChange={(e) => {
                                  const next = [...assertionsList];
                                  next[index].expected = e.target.value;
                                  setAssertionsList(next);
                                }}
                                className="realapi-kv-input"
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="realapi-kv-del-btn"
                                onClick={() => {
                                  const next = assertionsList.filter((_, idx) => idx !== index);
                                  setAssertionsList(next);
                                }}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <button
                      type="button"
                      className="realapi-link-btn"
                      onClick={() =>
                        setAssertionsList([
                          ...assertionsList,
                          {
                            id: "a_" + Date.now(),
                            target: "status_code",
                            operator: "equals",
                            expected: "200",
                            enabled: true
                          }
                        ])
                      }
                    >
                      + Add Assertion Rule
                    </button>
                  </div>
                )}

                {/* 6. SETTINGS TAB */}
                {requestTab === "settings" && (
                  <div className="realapi-auth-box">
                    <div style={{ marginBottom: "16px" }}>
                      <label style={{ fontSize: "12px", color: "#0f172a", fontWeight: 700, display: "block", marginBottom: "6px" }}>
                        Request Timeout (Seconds)
                      </label>
                      <input
                        type="number"
                        value={timeoutSeconds}
                        onChange={(e) => setTimeoutSeconds(e.target.value)}
                        min={1}
                        max={120}
                        className="realapi-kv-input"
                        style={{ width: "120px" }}
                      />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <input
                        type="checkbox"
                        id="verifySslCheckbox"
                        checked={verifySsl}
                        onChange={(e) => setVerifySsl(e.target.checked)}
                      />
                      <label htmlFor="verifySslCheckbox" style={{ fontSize: "13px", fontWeight: 600, color: "#334155", cursor: "pointer" }}>
                        Enable SSL / TLS Certificate Verification (Turn off for local self-signed dev APIs)
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT CARD: TELEMETRY & LIVE RESPONSE STUDIO */}
            <div className="realapi-card">
              <div className="realapi-card-header">
                <h3 className="realapi-card-title">
                  <span style={{ color: "#FD6101" }}>📊</span> Telemetry & Response
                </h3>

                {executionResult && (
                  <div className="realapi-sub-tabs">
                    <button
                      type="button"
                      className={`realapi-sub-tab ${responseTab === "body" ? "active" : ""}`}
                      onClick={() => setResponseTab("body")}
                    >
                      Body
                    </button>
                    <button
                      type="button"
                      className={`realapi-sub-tab ${responseTab === "waterfall" ? "active" : ""}`}
                      onClick={() => setResponseTab("waterfall")}
                    >
                      Socket Waterfall
                    </button>
                    <button
                      type="button"
                      className={`realapi-sub-tab ${responseTab === "headers" ? "active" : ""}`}
                      onClick={() => setResponseTab("headers")}
                    >
                      Headers
                      <span className="realapi-badge-count">
                        {Object.keys(executionResult.execution?.response?.headers || {}).length}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={`realapi-sub-tab ${responseTab === "assertions" ? "active" : ""}`}
                      onClick={() => setResponseTab("assertions")}
                    >
                      Assertions
                      {executionResult.assertions && (
                        <span
                          className="realapi-badge-count"
                          style={{
                            backgroundColor: executionResult.success ? "#10b981" : "#ef4444",
                            color: "#ffffff"
                          }}
                        >
                          {executionResult.assertions.filter((a) => a.passed).length}/
                          {executionResult.assertions.length}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`realapi-sub-tab ${responseTab === "code" ? "active" : ""}`}
                      onClick={() => setResponseTab("code")}
                    >
                      Code Snippet
                    </button>
                  </div>
                )}
              </div>

              <div className="realapi-card-body">
                {executionResult ? (
                  <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    {/* Live Telemetry Metrics Bar */}
                    <div className="realapi-telemetry-header">
                      <div className={`realapi-status-badge ${statusClass}`}>
                        <span>●</span>
                        <span>{executionResult.execution?.http_status || (executionResult.success ? "200 OK" : "ERROR")}</span>
                      </div>

                      <div className="realapi-metric-pill" title="Hardware Total Duration">
                        <span>⏱️</span>
                        <span>{metrics.total_duration_ms || 0} ms</span>
                      </div>

                      <div className="realapi-metric-pill" title="Response Payload Size">
                        <span>📦</span>
                        <span>{formatBytes(metrics.response_size_bytes)}</span>
                      </div>

                      {metrics.resolved_ip && (
                        <div className="realapi-metric-pill" title="Resolved Hardware Server IP">
                          <span>🌐</span>
                          <span>{metrics.resolved_ip}</span>
                        </div>
                      )}

                      <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                        <button
                          type="button"
                          className="realapi-preset-pill"
                          onClick={() => {
                            const raw = executionResult.execution?.response?.body_json
                              ? JSON.stringify(executionResult.execution.response.body_json, null, 2)
                              : executionResult.execution?.response?.body_raw || "";
                            copyToClipboard(raw, "response_body");
                          }}
                          title="Copy Response Body"
                        >
                          {copiedField === "response_body" ? "✓ Copied" : "📋 Copy"}
                        </button>

                        <button
                          type="button"
                          className="realapi-preset-pill"
                          onClick={() => {
                            const raw = executionResult.execution?.response?.body_json
                              ? JSON.stringify(executionResult.execution.response.body_json, null, 2)
                              : executionResult.execution?.response?.body_raw || "";
                            const blob = new Blob([raw], { type: "application/json" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `response_${Date.now()}.json`;
                            a.click();
                          }}
                          title="Download JSON Payload"
                        >
                          💾 Download
                        </button>
                      </div>
                    </div>

                    {/* Failure Guidance Banner if available */}
                    {executionResult.execution?.guidance && (
                      <div
                        style={{
                          backgroundColor: "#fef2f2",
                          border: "1px solid #fca5a5",
                          color: "#dc2626",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          marginBottom: "14px",
                          fontSize: "12.5px"
                        }}
                      >
                        <strong>🔍 Failure Guidance:</strong> {executionResult.execution.guidance}
                      </div>
                    )}

                    {/* SUB-TAB 1: RESPONSE BODY */}
                    {responseTab === "body" && (
                      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
                            Payload Inspector
                          </span>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              className={`realapi-preset-pill ${responseBodyView === "pretty" ? "active" : ""}`}
                              onClick={() => setResponseBodyView("pretty")}
                            >
                              Pretty
                            </button>
                            <button
                              type="button"
                              className={`realapi-preset-pill ${responseBodyView === "raw" ? "active" : ""}`}
                              onClick={() => setResponseBodyView("raw")}
                            >
                              Raw
                            </button>
                          </div>
                        </div>

                        <pre className="realapi-response-body-box">
                          {executionResult.execution?.response?.body_json
                            ? responseBodyView === "pretty"
                              ? JSON.stringify(executionResult.execution.response.body_json, null, 2)
                              : JSON.stringify(executionResult.execution.response.body_json)
                            : executionResult.execution?.response?.body_raw || executionResult.error || "No body content returned."}
                        </pre>
                      </div>
                    )}

                    {/* SUB-TAB 2: SOCKET WATERFALL */}
                    {responseTab === "waterfall" && (
                      <div className="realapi-waterfall-card">
                        <div className="realapi-waterfall-title">
                          <span>Direct Hardware Socket Timings</span>
                          <span style={{ fontSize: "10.5px", color: "#10b981", fontWeight: 700 }}>
                            Zero Simulation Mandate Verified
                          </span>
                        </div>

                        {[
                          { label: "1. DNS Lookup", ms: metrics.dns_lookup_ms || 0, color: "#0284c7" },
                          { label: "2. TCP Connect", ms: metrics.tcp_connect_ms || 0, color: "#FD6101" },
                          { label: "3. TLS Handshake", ms: metrics.tls_handshake_ms || 0, color: "#8b5cf6" },
                          { label: "4. Time to 1st Byte (TTFB)", ms: metrics.time_to_first_byte_ms || 0, color: "#10b981" },
                          { label: "5. Content Download", ms: metrics.download_time_ms || 0, color: "#06b6d4" },
                          { label: "Total Duration", ms: metrics.total_duration_ms || 0, color: "#1e293b", isTotal: true }
                        ].map((phase, idx) => {
                          const total = metrics.total_duration_ms || 1;
                          const percent = phase.isTotal ? 100 : Math.min(100, Math.max(4, (phase.ms / total) * 100));
                          return (
                            <div key={idx} className="realapi-waterfall-row">
                              <span className="realapi-waterfall-label">{phase.label}</span>
                              <div className="realapi-waterfall-track">
                                <div
                                  className="realapi-waterfall-bar"
                                  style={{
                                    width: `${percent}%`,
                                    backgroundColor: phase.color
                                  }}
                                />
                              </div>
                              <span className="realapi-waterfall-val">{phase.ms} ms</span>
                            </div>
                          );
                        })}

                        <div style={{ marginTop: "14px", padding: "10px 12px", background: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px", color: "#64748b" }}>
                          💡 <strong>Zero Simulation Guarantee:</strong> Each phase measurement reflects real physical socket handshakes captured directly at the OS level via non-blocking TCP socket probes.
                        </div>
                      </div>
                    )}

                    {/* SUB-TAB 3: RESPONSE HEADERS */}
                    {responseTab === "headers" && (
                      <div style={{ overflowY: "auto", flex: 1 }}>
                        <table className="realapi-kv-table">
                          <thead>
                            <tr>
                              <th>Header Name</th>
                              <th>Value</th>
                              <th style={{ width: "30px" }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(executionResult.execution?.response?.headers || {}).map(([key, val], idx) => (
                              <tr key={idx}>
                                <td style={{ fontWeight: 700, color: "#0f172a", fontSize: "12px" }}>{key}</td>
                                <td style={{ fontFamily: "monospace", fontSize: "11.5px", color: "#475569" }}>{String(val)}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="realapi-kv-del-btn"
                                    onClick={() => copyToClipboard(String(val), "hdr_" + idx)}
                                    title="Copy Value"
                                  >
                                    {copiedField === "hdr_" + idx ? "✓" : "📋"}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* SUB-TAB 4: ASSERTIONS TEST RESULTS */}
                    {responseTab === "assertions" && (
                      <div style={{ overflowY: "auto", flex: 1 }}>
                        {executionResult.assertions && executionResult.assertions.length > 0 ? (
                          executionResult.assertions.map((ass, idx) => (
                            <div
                              key={idx}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "10px",
                                border: `1px solid ${ass.passed ? "#a7f3d0" : "#fca5a5"}`,
                                backgroundColor: ass.passed ? "#ecfdf5" : "#fef2f2",
                                marginBottom: "10px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                              }}
                            >
                              <div>
                                <span style={{ fontWeight: 800, fontSize: "13px", color: ass.passed ? "#059669" : "#dc2626" }}>
                                  {ass.passed ? "✓ PASSED" : "✗ FAILED"}
                                </span>
                                <div style={{ fontSize: "12px", color: "#334155", marginTop: "3px" }}>
                                  Target: <strong>{ass.target}</strong> | Operator: <code>{ass.operator}</code>
                                </div>
                                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                                  Expected: <code>{String(ass.expected)}</code> | Actual: <code>{String(ass.actual)}</code>
                                </div>
                              </div>
                              <span
                                style={{
                                  fontSize: "11px",
                                  padding: "3px 8px",
                                  borderRadius: "6px",
                                  fontWeight: 700,
                                  backgroundColor: ass.passed ? "#10b981" : "#ef4444",
                                  color: "#ffffff"
                                }}
                              >
                                {ass.passed ? "Pass" : "Fail"}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: "30px 0", textAlign: "center", color: "#94a3b8" }}>
                            No assertions configured for this execution.
                          </div>
                        )}
                      </div>
                    )}

                    {/* SUB-TAB 5: CODE EXPORT */}
                    {responseTab === "code" && (
                      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
                          {[
                            { id: "curl", label: "cURL" },
                            { id: "fetch", label: "JavaScript (Fetch)" },
                            { id: "axios", label: "Axios (Node.js)" },
                            { id: "python", label: "Python (Requests)" },
                            { id: "go", label: "Go" }
                          ].map((lang) => (
                            <button
                              key={lang.id}
                              type="button"
                              className={`realapi-preset-pill ${codeLang === lang.id ? "active" : ""}`}
                              onClick={() => setCodeLang(lang.id)}
                            >
                              {lang.label}
                            </button>
                          ))}

                          <button
                            type="button"
                            className="realapi-link-btn"
                            style={{ marginLeft: "auto" }}
                            onClick={() => copyToClipboard(generatedCode, "code_export")}
                          >
                            {copiedField === "code_export" ? "✓ Copied Snippet" : "📋 Copy Snippet"}
                          </button>
                        </div>

                        <pre className="realapi-response-body-box" style={{ color: "#38bdf8" }}>
                          {generatedCode}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  /* 12. HIGH-END INTERACTIVE EMPTY STATE LAUNCHPAD */
                  <div className="realapi-empty-launchpad">
                    <div className="realapi-empty-icon">⚡</div>
                    <h4 className="realapi-empty-title">Ready for Real Network Execution</h4>
                    <p className="realapi-empty-subtext">
                      Click <strong>"Send Request"</strong> above or select a pre-configured quick-start template below to inspect hardware socket telemetry with zero simulation.
                    </p>

                    <div className="realapi-sample-cards">
                      {QUICK_PRESETS.map((sample) => (
                        <div
                          key={sample.id}
                          className="realapi-sample-card"
                          onClick={() => {
                            handleApplyPreset(sample);
                          }}
                          title={`Load ${sample.name}`}
                        >
                          <span
                            className="realapi-sample-method"
                            style={{
                              backgroundColor: sample.method === "POST" ? "#fff7ed" : "#ecfdf5",
                              color: sample.method === "POST" ? "#FD6101" : "#059669"
                            }}
                          >
                            {sample.method}
                          </span>
                          <div className="realapi-sample-title">{sample.name}</div>
                          <div className="realapi-sample-desc">{sample.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* 4. REAL LOAD TESTING TAB */}
      {activeTab === "load" && (
        <div className="realapi-card" style={{ padding: "26px" }}>
          <h3 style={{ margin: "0 0 14px 0", color: "#0f172a", fontWeight: 800, fontSize: "18px" }}>
            🔥 Real Multi-Threaded HTTP Load Tester
          </h3>
          <p style={{ color: "#64748b", fontSize: "13px", margin: "0 0 20px 0" }}>
            Generates concurrent socket threads to measure throughput, error rate, and p95/p99 latency against real hardware endpoints.
          </p>

          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "22px" }}>
            <div>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                Target URL
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="realapi-kv-input"
                style={{ width: "360px" }}
              />
            </div>

            <div>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                Concurrent Users
              </label>
              <input
                type="number"
                value={loadUsers}
                onChange={(e) => setLoadUsers(e.target.value)}
                min={1}
                max={100}
                className="realapi-kv-input"
                style={{ width: "130px" }}
              />
            </div>

            <div>
              <label style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
                Total Requests
              </label>
              <input
                type="number"
                value={loadRequests}
                onChange={(e) => setLoadRequests(e.target.value)}
                min={5}
                max={500}
                className="realapi-kv-input"
                style={{ width: "130px" }}
              />
            </div>

            <button
              type="button"
              className="realapi-send-btn"
              onClick={handleRunLoadTest}
              disabled={loading}
              style={{ background: "#dc2626" }}
              onMouseEnter={e => e.currentTarget.style.background = "#b91c1c"}
              onMouseLeave={e => e.currentTarget.style.background = "#dc2626"}
            >
              {loading ? "Running Load Test..." : "Start Real Load Test"}
            </button>
          </div>

          {loadResult && (
            <div style={{ backgroundColor: "#f8fafc", padding: "20px", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
              <h4 style={{ margin: "0 0 16px 0", color: "#FD6101", fontWeight: 800 }}>Real Load Metrics Result</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
                <div style={{ backgroundColor: "#ffffff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 700 }}>REQUESTS / SEC</div>
                  <div style={{ color: "#0284c7", fontWeight: 900, fontSize: "20px", marginTop: "4px" }}>
                    {loadResult.requests_per_second}
                  </div>
                </div>

                <div style={{ backgroundColor: "#ffffff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 700 }}>THROUGHPUT</div>
                  <div style={{ color: "#FD6101", fontWeight: 900, fontSize: "20px", marginTop: "4px" }}>
                    {loadResult.throughput_kbps} KB/s
                  </div>
                </div>

                <div style={{ backgroundColor: "#ffffff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 700 }}>ERROR RATE</div>
                  <div style={{ color: loadResult.error_rate_percent > 0 ? "#dc2626" : "#10b981", fontWeight: 900, fontSize: "20px", marginTop: "4px" }}>
                    {loadResult.error_rate_percent}%
                  </div>
                </div>

                <div style={{ backgroundColor: "#ffffff", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "11px", color: "#64748b", fontWeight: 700 }}>P95 LATENCY</div>
                  <div style={{ color: "#16a34a", fontWeight: 900, fontSize: "20px", marginTop: "4px" }}>
                    {loadResult.latency_percentiles_ms?.p95} ms
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5. OWASP SECURITY AUDIT TAB */}
      {activeTab === "security" && (
        <div className="realapi-card" style={{ padding: "26px" }}>
          <h3 style={{ margin: "0 0 14px 0", color: "#0f172a", fontWeight: 800, fontSize: "18px" }}>
            🛡️ OWASP API Security Audit Scanner
          </h3>
          <p style={{ color: "#64748b", fontSize: "13px", margin: "0 0 20px 0" }}>
            Performs non-destructive active probes against target endpoint for OWASP API Top 10 vulnerabilities (Injection, Broken Object Level Auth, Sensitive Data Exposure, Missing Headers).
          </p>

          <div style={{ display: "flex", gap: "12px", marginBottom: "22px" }}>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="realapi-kv-input"
              style={{ width: "420px" }}
            />
            <button
              type="button"
              className="realapi-send-btn"
              onClick={handleRunSecurityScan}
              disabled={loading}
              style={{ background: "#FD6101" }}
              onMouseEnter={e => e.currentTarget.style.background = "#e05300"}
              onMouseLeave={e => e.currentTarget.style.background = "#FD6101"}
            >
              {loading ? "Scanning Endpoint..." : "Run Security Audit"}
            </button>
          </div>

          {securityResult && (
            <div style={{ backgroundColor: "#f8fafc", padding: "20px", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <div>
                  <h4 style={{ margin: "0 0 4px 0", color: "#0f172a" }}>
                    Security Score:{" "}
                    <span
                      style={{
                        color:
                          securityResult.security_score >= 80
                            ? "#16a34a"
                            : securityResult.security_score >= 50
                              ? "#ea580c"
                              : "#dc2626"
                      }}
                    >
                      {securityResult.security_score} / 100
                    </span>
                  </h4>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Checks Performed: {securityResult.checks_performed} | Vulnerabilities Found:{" "}
                    {securityResult.vulnerabilities_found}
                  </span>
                </div>
              </div>

              {securityResult.vulnerabilities?.map((v, i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#ffffff",
                    padding: "16px",
                    borderRadius: "10px",
                    marginBottom: "12px",
                    borderLeft: `4px solid ${v.severity === "CRITICAL" || v.severity === "HIGH" ? "#dc2626" : "#ea580c"}`,
                    border: "1px solid #e2e8f0"
                  }}
                >
                  <strong style={{ color: v.severity === "CRITICAL" || v.severity === "HIGH" ? "#dc2626" : "#ea580c" }}>
                    [{v.severity}] {v.category}
                  </strong>
                  <div style={{ fontSize: "13px", marginTop: "6px", color: "#0f172a" }}>{v.finding}</div>
                  <div style={{ fontSize: "12px", color: "#64748b", marginTop: "6px" }}>
                    <strong>Remediation:</strong> {v.remediation}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 6. HISTORY FLYOUT DRAWER */}
      {showHistoryDrawer && (
        <div className="realapi-history-drawer">
          <div className="realapi-history-header">
            <h4 style={{ margin: 0, fontWeight: 800, fontSize: "15px", color: "#0f172a" }}>
              Execution History
            </h4>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                className="realapi-link-btn"
                onClick={() => {
                  setHistory([]);
                  localStorage.removeItem("qintra_real_api_history");
                }}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setShowHistoryDrawer(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#64748b" }}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="realapi-history-list">
            {history.length > 0 ? (
              history.map((item) => (
                <div
                  key={item.id}
                  className="realapi-history-item"
                  onClick={() => {
                    setMethod(item.method);
                    setUrl(item.url);
                    setShowHistoryDrawer(false);
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 800,
                        color: item.method === "POST" ? "#FD6101" : "#059669"
                      }}
                    >
                      {item.method}
                    </span>
                    <span style={{ fontSize: "10.5px", color: "#94a3b8" }}>{item.timestamp}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#0f172a", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.url}
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginTop: "6px", fontSize: "11px", color: "#64748b" }}>
                    <span>Status: {item.status}</span>
                    <span>•</span>
                    <span>{item.duration} ms</span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: "13px" }}>
                No past executions recorded yet.
              </div>
            )}
          </div>
        </div>
      )}

      {/* 7. MULTI-FORMAT IMPORT MODAL */}
      {showImportModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              padding: "26px",
              borderRadius: "18px",
              width: "560px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ margin: 0, color: "#0f172a", fontWeight: 800, fontSize: "16px" }}>
                Import API Test Request
              </h3>
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#64748b" }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: "flex", gap: "6px", marginBottom: "14px" }}>
              {[
                { id: "curl", label: "cURL Command" },
                { id: "postman", label: "Postman (v2.1)" },
                { id: "openapi", label: "OpenAPI / Swagger" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`realapi-preset-pill ${importType === tab.id ? "active" : ""}`}
                  onClick={() => setImportType(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <textarea
              value={importInput}
              onChange={(e) => setImportInput(e.target.value)}
              placeholder={
                importType === "curl"
                  ? "curl -X POST 'https://api.example.com/v1/user' -H 'Authorization: Bearer ...' -d '{\"name\": \"Alice\"}'"
                  : importType === "postman"
                    ? '{\n  "info": { "name": "Collection" },\n  "item": [...] \n}'
                    : '{\n  "openapi": "3.0.0",\n  "paths": { ... }\n}'
              }
              rows={8}
              className="realapi-body-editor"
              style={{ minHeight: "180px", marginBottom: "18px" }}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                className="realapi-action-btn"
                onClick={() => setShowImportModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="realapi-send-btn"
                onClick={handleExecuteImport}
                disabled={loading || !importInput.trim()}
              >
                {loading ? "Importing..." : "Parse & Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
